import React, { forwardRef, useId, useLayoutEffect, useRef, useState } from 'react';
import { dateError, birthDateError, birthDateBounds, displayDate, formatDateDigits, toISODate } from '../shared/dateValue';
import './DateInput.css';

// La saisie partielle reste un brouillon invalide ; seule une date réelle devient ISO.
const DateInput = forwardRef(function DateInput({ value = '', onChange, onKeyDown, lang = 'fr', birthDate = false, id, readOnly = false, disabled = false, className = '', ...props }, forwardedRef) {
  const generatedId = useId();
  const fieldId = id || `date-${generatedId}`;
  const hintId = `${fieldId}-format`;
  const input = useRef(null);
  const picker = useRef(null);
  const pendingCaret = useRef(null);
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const text = displayDate(value);
  const english = lang === 'en';
  const calendarLabel = english ? 'Choose a date' : 'Choisir une date';
  const invalid = birthDate ? birthDateError(value, lang) : dateError(value, lang);
  const bounds = birthDate ? birthDateBounds() : {};
  const description = [props['aria-describedby'], hintId].filter(Boolean).join(' ');

  useLayoutEffect(() => {
    input.current?.setCustomValidity(invalid);
    if (pendingCaret.current !== null && document.activeElement === input.current) {
      input.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
  });

  function commit(raw, digitPosition) {
    const formatted = formatDateDigits(raw);
    const digits = Math.min(digitPosition, formatted.replace(/\D/g, '').length);
    const caret = digits + (digits > 2 ? 1 : 0) + (digits > 4 ? 1 : 0);
    pendingCaret.current = caret;
    // Appliquer aussi au DOM si le parent conserve la même valeur (lettre rejetée).
    input.current.value = formatted;
    input.current.setSelectionRange(caret, caret);
    onChange(toISODate(formatted) || formatted);
  }
  function change(event) {
    const raw = event.target.value;
    const beforeCaret = raw.slice(0, event.target.selectionStart);
    commit(raw, beforeCaret.replace(/\D/g, '').length);
  }
  function keyDown(event) {
    onKeyDown?.(event);
    if (event.defaultPrevented || readOnly || disabled || event.ctrlKey || event.metaKey || event.altKey) return;
    const start = event.currentTarget.selectionStart;
    const end = event.currentTarget.selectionEnd;
    if (start !== end) return;
    // Effacer le chiffre voisin plutôt que bloquer le curseur sur un séparateur.
    if (event.key === 'Backspace' && text[start - 1] === '/') {
      event.preventDefault();
      commit(text.slice(0, start - 2) + text.slice(start), text.slice(0, start - 2).replace(/\D/g, '').length);
    } else if (event.key === 'Delete' && text[start] === '/') {
      event.preventDefault();
      commit(text.slice(0, start) + text.slice(start + 2), text.slice(0, start).replace(/\D/g, '').length);
    }
  }
  function paste(event) {
    if (readOnly || disabled) return;
    event.preventDefault();
    const raw = event.clipboardData.getData('text').trim();
    const pasted = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? displayDate(raw) : raw;
    const start = event.currentTarget.selectionStart;
    const end = event.currentTarget.selectionEnd;
    const before = text.slice(0, start);
    commit(before + pasted + text.slice(end), (before + pasted).replace(/\D/g, '').length);
  }
  function openCalendar() {
    if (readOnly || disabled) return;
    try {
      if (typeof picker.current.showPicker === 'function') { picker.current.showPicker(); return; }
    } catch { /* Le sélecteur natif reste accessible si showPicker est indisponible. */ }
    setFallbackOpen(previous => !previous);
  }
  return <div className="psi-date-field">
    <div className="psi-date-control">
      <input {...props} ref={element => { input.current = element; if (typeof forwardedRef === 'function') forwardedRef(element); else if (forwardedRef) forwardedRef.current = element; }}
        id={fieldId} type="text" inputMode="numeric" autoComplete="off" lang={lang} value={text}
        className={`text ${className}`} placeholder={english ? 'DD/MM/YYYY' : 'JJ/MM/AAAA'}
        readOnly={readOnly} disabled={disabled} onChange={change} onKeyDown={keyDown} onPaste={paste}
        aria-invalid={Boolean(invalid) || props['aria-invalid']} aria-describedby={description} />
      <button type="button" className="psi-date-calendar" onClick={openCalendar} disabled={readOnly || disabled} aria-label={calendarLabel} title={calendarLabel}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4M17 3v4M3 11h18M7 15h2M11 15h2M15 15h2M7 18h2M11 18h2" /></svg>
      </button>
      <input ref={picker} type="date" min={bounds.min} max={bounds.max} className={`psi-date-picker${fallbackOpen ? ' is-open' : ''}`} value={toISODate(value)}
        disabled={readOnly || disabled} tabIndex={fallbackOpen ? 0 : -1} aria-label={calendarLabel} lang={lang}
        onChange={event => { onChange(event.target.value); setFallbackOpen(false); input.current?.focus(); }} />
    </div>
    <small id={hintId} className="psi-date-hint">{english ? 'DD/MM/YYYY · Enter digits only, or use the calendar.' : 'JJ/MM/AAAA · Saisissez uniquement les chiffres ou utilisez le calendrier.'}{birthDate && (english ? ' Age: 13–99 years.' : ' Âge : de 13 à 99 ans.')}</small>
  </div>;
});
export default DateInput;
